'use client';

import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { useQuery } from '@tanstack/react-query';
import { Carousel } from '@mantine/carousel';
import { HabitCard } from '../HabitCard/HabitCard';
import { StreakCard } from '../StreakCard/StreakCard';
import { HabitSelector } from '../../HabitSelector';
import { HabitTrackingModal } from '../../habitrackingModal/HabitTrackingModal';
import styles from './Dashboard.module.scss';
import { DashboardResponse, UserHabitResponse, ActionTypePickerResponse } from '../../../api/Api';
import { useApiClient } from '../../../api/ApiClientContext';
import { SummaryBlocks } from '../SummaryBlocks';
import { TasksCard } from '../TasksCard';
import { EndorseCard } from '../EndorseCard';
import { Icon } from '../../../assets/icons/Icon';
import '@mantine/carousel/styles.css';
import { BE_SECURE_HABIT_ID } from '../../../constants/habits';

interface DashboardProps {
  data: DashboardResponse;
  className?: string;
  active: UserHabitResponse[];
}

export const Dashboard: React.FC<DashboardProps> = ({ data, active, className = '' }) => {
  // Check if Be Secure habit is already active (don't show initiative task if so)
  const isBeSecureHabitActive = active?.some(habit => habit.habit?.id === BE_SECURE_HABIT_ID);
  const [isHabitSelectorOpen, setIsHabitSelectorOpen] = useState(false);
  const [actionTypesMap, setActionTypesMap] = useState<Record<string, ActionTypePickerResponse>>({});
  const [trackingModalHabitId, setTrackingModalHabitId] = useState<string | null>(null);
  const apiClient = useApiClient();

  // Fetch progress data for the Progress card
  const { data: progressData } = useQuery({
    queryKey: ['dashboardProgress'],
    queryFn: async () => {
      const res = await apiClient.api.getProgressChartDataApiV1DashboardProgressGet();
      return res?.data;
    },
  });

  const activeHabitsWithStartDate = data.active_habits.map((habit) => {
    const activeHabit = active?.find((a: any) => a.id === habit.id);
    return {
      ...habit,
      habit_icon_url: habit.habit_icon_url || null,
      last_logged_date: habit.last_logged_date || null,
      started_at: activeHabit?.started_at || null,
      description: activeHabit?.habit?.description,
      instructions: activeHabit?.habit?.instructions,
      module_name: activeHabit?.habit?.module_name || (habit as any).module_name,
      behaviour_name: activeHabit?.habit?.behaviour_name || (habit as any).behaviour_name,
      programme_name: activeHabit?.habit?.programme_name || (habit as any).programme_name,
    };
  });

  // Fetch action types for habits with show_action: true
  useEffect(() => {
    const fetchActionTypes = async () => {
      const newActionTypesMap: Record<string, ActionTypePickerResponse> = {};
      for (const activeHabit of active || []) {
        if (activeHabit.show_action) {
          try {
            const res = await apiClient.api.getActionTypesForPickerApiV1HabitsHabitIdActionTypesGet(
              activeHabit.habit.id
            );
            console.log(`Action types for habit ${activeHabit.habit.id}:`, res.data);
            newActionTypesMap[activeHabit.id] = res.data;
          } catch (error) {
            console.error(`Error fetching action types for habit ${activeHabit.habit.id}:`, error);
          }
        }
      }
      setActionTypesMap(newActionTypesMap);
    };
    fetchActionTypes();
  }, [active, apiClient]);

  // Get overall daily completion data for Progress card (from dedicated progress API)
  const overallDailyCompletion = progressData?.daily_completion || {};

  return (
    <div className={`${styles.dashboard} ${className}`}>
      <main className={styles.dashboard__body} role="main">

        {/* Summary Blocks - Daily Mood, Achievements, Team */}
        <SummaryBlocks moodWidget={data.mood_widget} />

        {/* Title Row - Full Width */}
        <div className={styles.dashboard__titleRow}>
          <h1 className={styles.dashboard__pageTitle}>Habits</h1>
          <span className={styles.dashboard__currentDate}>
            {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </span>
        </div>

        {/* Main Grid: Habits on left, Carousel on right */}
        <div className={styles.dashboard__grid}>
          {/* Habit Cards - Left Column */}
          <section className={styles.dashboard__habitsBlock} aria-label="Habits tracking">
            {activeHabitsWithStartDate.length > 0 ? (
              activeHabitsWithStartDate.map((habit) => {
                const activeHabit = active?.find((a) => a.id === habit.id);
                return (
                  <HabitCard
                    key={habit.id}
                    habit={habit}
                    programmeName={habit.programme_name || ''}
                    className={styles.dashboard__habitCard}
                    showAction={activeHabit?.show_action}
                    actionTypes={actionTypesMap[habit.id]}
                  />
                );
              })
            ) : (
              <div className={styles.dashboard__emptyHabits}>
                <div className={styles.dashboard__emptyIconWrapper}>
                  <Icon iconName="GreyCalendarIcon" width="5rem" height="5rem" />
                </div>
                <h3 className={styles.dashboard__emptyTitle}>No active habits</h3>
                <p className={styles.dashboard__emptyText}>
                  You do not have active habits at the moment. New habits will be available once next module starts.
                </p>
              </div>
            )}
          </section>

          {/* Right Column - Progress Card */}
          <div className={styles.dashboard__rightColumn}>
            {/* Progress Card - Aggregate Streak */}
            <StreakCard 
              currentStreak={(progressData?.overall_stats as any)?.current_streak || 0}
              dailyCompletion={overallDailyCompletion}
              streakRanges={(progressData?.overall_stats as any)?.streak_ranges || []}
            />

            {/* Tasks Carousel - Tasks & Endorse */}
            {(() => {
              // Count number of slides in this carousel
              const taskCarouselSlideCount = (isBeSecureHabitActive ? 0 : 1) + 1; // TasksCard (conditional) + EndorseCard
              const showTaskCarouselNav = taskCarouselSlideCount > 1;
              
              return (
                <div className={styles.dashboard__carouselWrapper}>
                  <Carousel
                    slideSize="100%"
                    withControls={showTaskCarouselNav}
                    withIndicators={showTaskCarouselNav}
                    classNames={{
                      root: styles.dashboard__carousel,
                      slide: styles.dashboard__carouselSlide,
                      control: styles.dashboard__carouselControl,
                      indicators: styles.dashboard__carouselIndicators,
                      indicator: styles.dashboard__carouselIndicator,
                    }}
                    styles={{
                      indicator: {
                        backgroundColor: '#CED4DA',
                        '&[data-active]': {
                          backgroundColor: '#4C6EF5',
                        },
                      },
                    }}
                  >
                    {!isBeSecureHabitActive && (
                      <Carousel.Slide>
                        <TasksCard />
                      </Carousel.Slide>
                    )}
                    <Carousel.Slide>
                      <EndorseCard />
                    </Carousel.Slide>
                  </Carousel>
                </div>
              );
            })()}
          </div>
        </div>
      </main>

      <Modal
        isOpen={isHabitSelectorOpen}
        onRequestClose={() => setIsHabitSelectorOpen(false)}
        contentLabel="Select Habits"
        ariaHideApp={false}
        style={{
          overlay: { backgroundColor: 'rgba(16, 20, 48, 0.50)', zIndex: 1000 },
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            borderRadius: '0.75rem',
            border: 'none',
            maxWidth: '700px',
            padding: '2rem',
          },
        }}
      >
        <HabitSelector onClose={() => setIsHabitSelectorOpen(false)} />
      </Modal>

      {/* Habit Tracking Modal for Streak Card */}
      {trackingModalHabitId && (() => {
        const activeHabit = active?.find((a) => a.id === trackingModalHabitId);
        const habitCard = activeHabitsWithStartDate.find((h) => h.id === trackingModalHabitId);
        if (!activeHabit || !habitCard) return null;
        return (
          <HabitTrackingModal
            isOpen={!!trackingModalHabitId}
            onClose={() => setTrackingModalHabitId(null)}
            habitData={activeHabit as any}
            habitTitle={habitCard.habit_name}
            habitBehavior={activeHabit.habit?.instructions || undefined}
          />
        );
      })()}
    </div>
  );
};
